import { Link, useLocation } from "react-router-dom";
import styles from "./FileExplorer.module.css";
import Icon from "../../../components/Icon/Icon";
import SelectElement, {
  type SelectListItemType,
} from "../../../components/SelectElement/SelectElement";
import { useEffect, useState, type JSX } from "react";
import DocumentsTable from "../../../components/DocumentsSettings/DocumentsTable/DocumentsTable";
import Pagination from "../../../components/DocumentsSettings/DocumentsTable/Pagination/Pagination";
import Calendar from "../../../components/Calendar/Calendar";
import SearchBar from "../../../components/DocumentsSettings/SearchBar/SearchBar";
import api from "../../../api/api";
import type {
  IGetAllDocumentsParams,
  IGetAllDocumentsResponse,
} from "../../../shared/types/documents.types";
import { endOfDay, format, formatISO, startOfDay } from "date-fns";
import { type IGetAllDocumentsTypesResponse } from "../../../shared/types/templates.types";
import type { GetAllDepartmentsListType } from "../../../shared/types/departments.types";
import ExportFilesModal from "../../../components/ExportFilesModal/ExportFilesModal";

const titles = [
  "Nombre de documento",
  "Departamento",
  "Numero de expediente",
  "Fecha de inclusión",
  "Previsualización",
];

type RowType = {
  id: string;
  properties: (string | number | JSX.Element)[];
};

type DocumentItemType = IGetAllDocumentsResponse["data"][number];

const FileExplorer = () => {
  const location = useLocation();
  const from = location.state?.from || "/";

  const [filterById, setFilterById] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedDateFrom, setSelectedDateFrom] = useState<Date | undefined>();
  const [selectedDateTo, setSelectedDateTo] = useState<Date | undefined>();

  const [selectedType, setSelectedType] = useState<SelectListItemType>({
    id: "1",
    element: <>Tipo de Documento</>,
  });
  const [selectedDepartment, setSelectedDepartment] =
    useState<SelectListItemType>({
      id: "1",
      element: <>Departamento</>,
    });

  const selectType = (option: SelectListItemType) => {
    setSelectedType(option);
  };

  const selectDepartment = (option: SelectListItemType) => {
    setSelectedDepartment(option);
  };

  const [currentPage, setCurrentPage] = useState(1);

  const [documentsResponse, setDocumentsResponse] =
    useState<IGetAllDocumentsResponse | null>(null);

  const [tableRows, setTableRows] = useState<RowType[]>([]);

  const [documentsTypes, setDocumentsTypes] =
    useState<IGetAllDocumentsTypesResponse | null>(null);

  useEffect(() => {
    const fetchDocumentsTypes = async () => {
      const data = await api.getAllDocumentsTypes();
      setDocumentsTypes(data);
    };
    fetchDocumentsTypes();
  }, []);

  const [departments, setDepartments] =
    useState<GetAllDepartmentsListType | null>(null);
  useEffect(() => {
    const fetchDepartments = async () => {
      const data = await api.getAllDepartmentsList();
      setDepartments(data);
    };
    fetchDepartments();
  }, []);

  const handleGetTemporaryLink = async (doc: DocumentItemType) => {
    const link = await api.getTemporaryLinkToFile({ fileUrl: doc.fileUrl });
    window.open(link.signedUrl);
  };

  useEffect(() => {
    const handleFetchDocuments = async (params: IGetAllDocumentsParams) => {
      try {
        const data = await api.getAllDocuments(params);
        setDocumentsResponse(data);
        const documentsToTable = data.data.map((doc) => {
          const date = format(new Date(doc.uploadedAt), "dd/MM/yy");

          return {
            id: doc.id,
            properties: [
              doc.documentTypeName || "None",
              doc.departmentCode,
              <Link to={`/review-document/${doc.id}`}>
                {doc.expedientNumber}
              </Link>,
              <p className={styles.centered}>{date}</p>,
              <p
                onClick={() => {
                  handleGetTemporaryLink(doc);
                }}
                className={styles.docLink}
              >
                <Icon name="document-visualize" size={32} />
              </p>,
            ],
          };
        });
        setTableRows(documentsToTable);
      } catch (error) {
        console.log(error);
      }
    };

    // Convert dates to UTC midnight to ensure consistent date handling
    const dateFrom = selectedDateFrom
      ? formatISO(startOfDay(selectedDateFrom))
      : undefined;

    const dateTo = selectedDateTo
      ? formatISO(endOfDay(selectedDateTo))
      : undefined;

    const typeId = selectedType.id === "1" ? undefined : selectedType.id;
    const departmentId =
      selectedDepartment.id === "1" ? undefined : selectedDepartment.id;

    handleFetchDocuments({
      limit: 8,
      page: currentPage,
      expedientNumber: filterById,
      dateFrom,
      dateTo,
      typeId,
      departmentId,
    });
  }, [
    currentPage,
    filterById,
    selectedDateFrom,
    selectedDateTo,
    selectedType,
    selectedDepartment,
  ]);

  const handleClearFilters = () => {
    setFilterById("");
    setSelectedType({ id: "1", element: <>Tipo de Documento</> });
    setSelectedDepartment({ id: "1", element: <>Departamento</> });
    setSelectedDateFrom(undefined);
    setSelectedDateTo(undefined);
  };

  return (
    <>
      <div className={styles.container}>
        <Link className={styles.getBackLink} to={from}>
          <Icon name="get-back-arrow" size={20} /> Volver a la biblioteca
        </Link>
        <h1 className={styles.pageTitle}>Explorador de archivos</h1>

        <div className={styles.upperHeadBlockContainer}>
          <div className={styles.selectersContainer}>
            <SelectElement
              options={
                documentsTypes &&
                documentsTypes?.map((type) => {
                  return {
                    id: type.id,
                    element: (
                      <p className={styles.selectListItem}>{type.name}</p>
                    ),
                  };
                })
              }
              setSelected={selectType}
              selected={selectedType}
              className={styles.selectElement}
              dropdownItemClassName={styles.selectElementDropdownItem}
              dropdownClassName={styles.selectElementDropdown}
            />
            <SelectElement
              options={
                departments &&
                departments.map((d) => {
                  return {
                    id: d.id,
                    element: <p className={styles.selectListItem}>{d.code}</p>,
                  };
                })
              }
              setSelected={selectDepartment}
              selected={selectedDepartment}
              className={styles.selectElement}
              dropdownItemClassName={styles.selectElementDropdownItem}
              dropdownClassName={styles.selectElementDropdown}
            />
          </div>
          <SearchBar
            placeholder="Buscar documento"
            handleSearch={(value) => {
              if (value !== "") {
                setCurrentPage(1);
              }
              setFilterById(value);
            }}
          />
        </div>
        <div className={styles.lowerHeadBlockContainer}>
          <div className={styles.datePickerAndResetContainer}>
            <div className={styles.datePickerContainer}>
              Fecha{" "}
              <Calendar
                maxDate={selectedDateTo}
                selectedDate={selectedDateFrom}
                setSelectedDate={setSelectedDateFrom}
              />{" "}
              a{" "}
              <Calendar
                minDate={selectedDateFrom}
                selectedDate={selectedDateTo}
                setSelectedDate={setSelectedDateTo}
              />
            </div>
            <button className={styles.clearFilter} onClick={handleClearFilters}>
              Limpiar filtros <Icon name="filter" size={20} />
            </button>
          </div>
          <button
            className={styles.exportButton}
            disabled={!documentsResponse?.total}
            onClick={() => {
              setIsModalOpen(true);
            }}
          >
            <span>Exportar archivos</span> <Icon name="export" size={20} />
          </button>
        </div>

        {documentsResponse && documentsResponse?.total >= 1 && (
          <div className={styles.explorerTableContainer}>
            <DocumentsTable titles={titles} documents={tableRows} />
            <Pagination
              totalPages={documentsResponse.totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
      <ExportFilesModal
        isOpen={isModalOpen}
        onRequestClose={() => {
          setIsModalOpen(false);
        }}
      />
    </>
  );
};

export default FileExplorer;
